/**
 * Candidate Service
 * 
 * This service handles all candidate-related API calls.
 * It uses the privateApiClient for authenticated requests.
 */

import { privateApiClient, handleApiError } from '../apiClient';
import API from '../../config/api';

/**
 * Get all candidates with optional filters
 * @param {Object} filters - Optional filters for the query
 * @param {string} filters.status - Filter by status (draft, submitted, pending, shortlisted, accepted, rejected, returned)
 * @param {number|string} filters.branchId - Filter by branch identifier
 * @param {number|string} filters.centerId - Filter by center identifier
 * @param {string} filters.search - Search term for candidate name or ID (client-side search is also applied)
 * @param {number} filters.skip - Offset for server-side pagination
 * @param {number} filters.limit - Limit for server-side pagination (backend default is 100)
 * @returns {Promise<Object>} Object with data property containing list of candidates
 */
export const getAllCandidates = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Add filters to query params if provided
    if (filters.status) params.append('status', filters.status);
    if (filters.branchId) params.append('branch_id', filters.branchId);
    if (filters.centerId) params.append('center_id', filters.centerId);
    if (filters.search) params.append('search', filters.search);
    if (typeof filters.skip === 'number') params.append('skip', String(filters.skip));
    if (typeof filters.limit === 'number') params.append('limit', String(filters.limit));
    
    const response = await privateApiClient.get(API.nominations.base, { params });
    // API returns an array directly, wrap it in an object with data property as expected by CandidatesList.js
    return { data: response.data };
  } catch (error) {
    return handleApiError(error, 'Failed to fetch candidates');
  }
};

/**
 * Get total count of candidates matching filters (for server-side pagination)
 * @param {Object} filters - Same filters as getAllCandidates (except skip/limit)
 * @returns {Promise<number>} total count
 */
export const getCandidatesCount = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.branchId) params.append('branch_id', filters.branchId);
    if (filters.centerId) params.append('center_id', filters.centerId);
    if (filters.search) params.append('search', filters.search);

    const response = await privateApiClient.get(`${API.nominations.base}/count`, { params });
    return response.data?.total ?? 0;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch candidates count');
  }
};

/**
 * Get a single candidate by ID
 * @param {string|number} id - Candidate ID
 * @returns {Promise<Object>} Object with data property containing candidate details
 */
export const getCandidateById = async (id) => {
  try {
    const response = await privateApiClient.get(API.nominations.getById(id));
    // Maintain consistent response structure with getAllCandidates
    return { data: response.data };
  } catch (error) {
    return handleApiError(error, `Failed to fetch candidate with ID: ${id}`);
  }
};

/**
 * Update a candidate's status
 * @param {string|number} id - Candidate ID
 * @param {string} status - New status (draft, submitted, pending, shortlisted, accepted, rejected, returned)
 * @param {string} notes - Optional notes about the status change
 * @returns {Promise<Object>} Object with data property containing updated candidate data
 */
export const updateCandidateStatus = async (id, status, notes = '') => {
  try {
    const formData = new FormData();
    formData.append('status', status);
    if (notes) formData.append('notes', notes);
    
    // When sending FormData, we need to let the browser set the Content-Type header
    // to include the correct boundary parameter
    // Use PUT method to match backend API implementation
    const response = await privateApiClient.put(
      `${API.nominations.base}/${id}/status`,
      formData,
      {
        headers: {
          'Content-Type': undefined // Let Axios set the correct multipart/form-data header
        }
      }
    );
    return { data: response.data };
  } catch (error) {
    return handleApiError(error, `Failed to update status for candidate ID: ${id}`);
  }
};

/**
 * Delete a candidate
 * @param {string|number} id - Candidate ID
 * @param {Object} options - Optional safer deletion options
 * @param {string} [options.reason] - Reason for deletion (sent as header X-Delete-Reason)
 * @param {boolean} [options.force] - Force deletion even if related data exists (sent as query param force=true)
 * @returns {Promise<Object>} Deletion status
 */
export const deleteCandidate = async (id, options = {}) => {
  try {
    const { reason, force } = options || {};
    const config = {};

    // Attach optional headers
    if (reason) {
      config.headers = { 'X-Delete-Reason': reason };
    }
    // Attach optional query params
    if (typeof force !== 'undefined') {
      config.params = { force: !!force };
    }

    const response = await privateApiClient.delete(API.nominations.delete(id), config);
    return response.data;
  } catch (error) {
    return handleApiError(error, `Failed to delete candidate with ID: ${id}`);
  }
};

/**
 * Check if a candidate (nomination) has evaluations
 * @param {string|number} id - Candidate ID
 * @returns {Promise<{has_evaluations: boolean, count: number}>}
 */
export const hasEvaluations = async (id) => {
  try {
    const response = await privateApiClient.get(API.nominations.evaluationsExists(id));
    return response.data;
  } catch (error) {
    return handleApiError(error, `Failed to check evaluations for candidate ID: ${id}`);
  }
};

/**
 * Export candidates data
 * @param {Object} filters - Filters for the export
 * @returns {Promise<Blob>} Exported file data
 */
export const exportCandidates = async (filters = {}) => {
  try {
    // If a specific candidate ID is provided, use the profile PDF endpoint
    if (filters.id) {
      const response = await privateApiClient.get(API.profiles.pdf(filters.id), {
        responseType: 'blob'
      });
      return response.data;
    }
    
    // Otherwise use general export endpoint
    const params = new URLSearchParams();
    
    // Add filters to query params if provided
    if (filters.status) params.append('status', filters.status);
    if (filters.branchId) params.append('branch_id', filters.branchId);
    if (filters.centerId) params.append('center_id', filters.centerId);
    if (filters.format) params.append('format', filters.format);
    
    const response = await privateApiClient.get(`${API.nominations.base}/export`, {
      params,
      responseType: 'blob'
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to export candidates');
  }
};

// Export all functions as a single object
const candidateService = {
  getAllCandidates,
  getCandidatesCount,
  getCandidateById,
  updateCandidateStatus,
  deleteCandidate,
  hasEvaluations,
  exportCandidates
};

export default candidateService;
